import { useEffect, useRef, useState } from "react";
import { Send, MapPin, Hash, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, getOrCreateSessionId } from "@/lib/api";
import VoiceRecorder, { RecordingIndicator } from "@/components/VoiceRecorder";
import ExtractedCard from "@/components/ExtractedCard";
import PartCard from "@/components/PartCard";
import VinDialog from "@/components/VinDialog";
import HandoffDialog from "@/components/HandoffDialog";
import { track } from "@/lib/analytics";

const QUICK_PICKS = [
  "Need a starter for a 2018 Hilux GD6",
  "Looking for a Ranger 3.2 gearbox 2017 automatic",
  "Polo 1.4 brake pads front",
  "Quantum 2.5 D-4D clutch kit",
  "Isuzu KB250 injectors",
  "Sprinter 313 CDI water pump",
];

const SA_CITIES = [
  "", "Johannesburg", "Cape Town", "Durban", "Pretoria",
  "Port Elizabeth", "Bloemfontein", "East London", "Polokwane",
  "Nelspruit", "Kimberley",
];

const Loader = () => (
  <div className="flex items-center gap-3 text-xs font-mono-tech text-neutral-500">
    <span>[</span>
    <div className="scan-bar w-32" />
    <span>] SEARCHING SA SUPPLIERS...</span>
  </div>
);

export default function ChatInterface() {
  const [sessionId] = useState(() => getOrCreateSessionId());
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]); // {role, text, extracted, matches, best_match_id, followup}
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [location, setLocation] = useState(() => localStorage.getItem("spannr_location") || "");
  const [vinOpen, setVinOpen] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("spannr_location", location || "");
  }, [location]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat/message", {
        message,
        session_id: sessionId,
        location: location || null,
      });
      track("search", {
        search_term: message.slice(0, 100),
        location: location || "any",
        matches: (data.matches || []).length,
        make: data.extracted?.vehicle?.make || null,
        model: data.extracted?.vehicle?.model || null,
      });
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: data.reply,
          extracted: data.extracted,
          matches: data.matches || [],
          best_match_id: data.best_match_id,
          followup: data.followup_question,
        },
      ]);
    } catch (e) {
      toast.error("Could not reach AI assistant");
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: "⚠ Connection issue. Please try again." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onTranscribed = (text) => {
    setInput((prev) => (prev ? prev + " " + text : text));
  };

  const isEmpty = messages.length === 0;

  // Composer — used in both empty (inline hero) and chat (bottom-pinned) states.
  const composer = (
    <div className="w-full">
      <div className="flex items-center gap-3 flex-wrap mb-2 text-xs">
        <div className="flex items-center gap-2 text-neutral-500">
          <MapPin size={12} />
          <span className="font-mono-tech tracking-wider uppercase">Your city</span>
        </div>
        <select
          data-testid="chat-location-select"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className="bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-2 h-8 text-xs"
        >
          {SA_CITIES.map((c) => (
            <option key={c} value={c}>{c || "Anywhere in SA"}</option>
          ))}
        </select>
        <button
          type="button"
          data-testid="chat-vin-button"
          onClick={() => setVinOpen(true)}
          className="flex items-center gap-1.5 h-8 px-2 border border-neutral-200 hover:border-neutral-300 text-neutral-700 transition-colors"
        >
          <Hash size={12} />
          <span className="font-mono-tech tracking-wider uppercase">VIN Decode</span>
        </button>
        {messages.some((m) => m.role === "assistant" && m.matches && m.matches.length > 0) && (
          <button
            type="button"
            data-testid="chat-handoff-button"
            onClick={() => setHandoffOpen(true)}
            className="flex items-center gap-1.5 h-8 px-2 bg-[#25D366] hover:bg-[#1EBE5D] text-black transition-colors"
          >
            <MessageSquare size={12} />
            <span className="font-mono-tech tracking-wider uppercase font-bold">Continue on WhatsApp</span>
          </button>
        )}
      </div>
      {recording && <RecordingIndicator />}
      <div className="flex items-end gap-2 mt-2">
        <textarea
          data-testid="chat-input-field"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={isEmpty ? 3 : 1}
          placeholder="Describe the part you need. e.g., Starter for 2018 Hilux GD6"
          className={`flex-1 resize-none bg-white border-2 ${isEmpty ? "border-neutral-300" : "border-neutral-200"} focus:border-[#FF5100] outline-none px-4 py-3 ${isEmpty ? "text-base leading-snug" : "text-sm"} text-neutral-900 placeholder:text-neutral-400 ${isEmpty ? "min-h-[88px]" : "min-h-[52px]"} max-h-40 transition-colors`}
          disabled={busy}
          autoFocus={isEmpty}
        />
        <VoiceRecorder
          disabled={busy}
          onTranscribed={onTranscribed}
          onRecordingChange={setRecording}
        />
        <button
          type="button"
          data-testid="chat-send-button"
          onClick={() => send()}
          disabled={busy || !input.trim()}
          className="h-12 px-5 bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-40 disabled:hover:bg-[#FF5100] text-white font-bold uppercase tracking-wider text-xs flex items-center gap-2 transition-colors"
        >
          <Send size={14} /> Send
        </button>
      </div>
      <div className="text-[10px] font-mono-tech text-neutral-500 mt-2 px-1">
        ENTER to send · SHIFT+ENTER for new line · Voice notes supported
      </div>
    </div>
  );

  // ── Empty state: hero + inline prominent search bar + quick picks ──
  if (isEmpty) {
    return (
      <div className="min-h-[calc(100vh-3.5rem)] bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-32">
          <div data-testid="empty-state">
            <div className="text-xs font-mono-tech tracking-[0.25em] text-[#FF5100] mb-3">
              READY · SA PARTS NETWORK
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.95] tracking-tight text-neutral-900">
              Find the right part.<br />
              <span className="text-[#FF5100]">In minutes, not hours.</span>
            </h1>
            <p className="mt-4 text-neutral-600 max-w-xl text-sm sm:text-base">
              Type or send a voice note like a WhatsApp. Our AI extracts vehicle &amp;
              part info, then searches scrapyards, dealerships and aftermarket suppliers across South&nbsp;Africa.
            </p>

            {/* PROMINENT SEARCH BAR — directly in the hero */}
            <div className="mt-6 sm:mt-8">
              {composer}
            </div>

            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-2">
                Or try a quick pick
              </div>
              <div className="flex flex-wrap gap-2">
                {QUICK_PICKS.map((q, i) => (
                  <button
                    key={q}
                    data-testid={`quick-pick-${i}`}
                    onClick={() => send(q)}
                    className="text-xs px-3 py-1.5 border border-neutral-200 hover:border-[#FF5100] hover:text-[#FF5100] transition-colors text-neutral-700"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {busy && (
              <div className="mt-8"><Loader /></div>
            )}
          </div>
        </div>

        {vinOpen && (
          <VinDialog
            onClose={() => setVinOpen(false)}
            onApply={(vehicle) => {
              const parts = [];
              if (vehicle.year) parts.push(vehicle.year);
              if (vehicle.make) parts.push(vehicle.make);
              if (vehicle.model) parts.push(vehicle.model);
              if (vehicle.engine) parts.push(`(${vehicle.engine})`);
              const prefix = parts.join(" ");
              setInput((cur) => (prefix ? `${prefix} — ` : "") + cur);
              setVinOpen(false);
            }}
          />
        )}
        {handoffOpen && (
          <HandoffDialog sessionId={sessionId} onClose={() => setHandoffOpen(false)} />
        )}
      </div>
    );
  }

  // ── Chat state: messages scrollable + composer pinned at bottom ──
  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]">
      {/* Messages area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {messages.map((m, idx) => (
            <div key={idx} className="space-y-3">
              {m.role === "user" ? (
                <div className="flex justify-end">
                  <div
                    data-testid={`user-message-${idx}`}
                    className="max-w-[80%] bg-neutral-900 text-white px-4 py-2.5 text-sm leading-relaxed"
                  >
                    {m.text}
                  </div>
                </div>
              ) : (
                <div data-testid={`assistant-message-${idx}`} className="space-y-4">
                  {m.extracted && <ExtractedCard extracted={m.extracted} />}
                  <div className="text-[15px] leading-relaxed text-neutral-900 whitespace-pre-wrap">
                    {m.text}
                  </div>
                  {m.matches && m.matches.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-3">
                        {m.matches.length} matches in SA supplier network
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {m.matches.map((p, i) => (
                          <PartCard
                            key={p.id}
                            part={p}
                            index={i}
                            isBest={p.id === m.best_match_id}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {busy && (
            <div className="py-2">
              <Loader />
            </div>
          )}
        </div>
      </div>

      {/* Composer — pinned at bottom in chat state. Extra bottom padding on mobile to clear browser UI / Emergent badge. */}
      <div className="border-t border-neutral-200 bg-white pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 pb-5 sm:pb-3">
          {composer}
        </div>
      </div>

      {vinOpen && (
        <VinDialog
          onClose={() => setVinOpen(false)}
          onApply={(vehicle) => {
            const parts = [];
            if (vehicle.year) parts.push(vehicle.year);
            if (vehicle.make) parts.push(vehicle.make);
            if (vehicle.model) parts.push(vehicle.model);
            if (vehicle.engine) parts.push(`(${vehicle.engine})`);
            const prefix = parts.join(" ");
            setInput((cur) => (prefix ? `${prefix} — ` : "") + cur);
            setVinOpen(false);
          }}
        />
      )}
      {handoffOpen && (
        <HandoffDialog
          sessionId={sessionId}
          onClose={() => setHandoffOpen(false)}
        />
      )}
    </div>
  );
}

// VoiceRecorder reports its recording state via onRecordingChange.
